import classNames from "classnames"
import React from "react"

import { Caption } from "@bounce/design-system"
import { intlNumberFormat, Trans, useBounceGlobalValues, useTranslation } from "@bounce/i18n"
import { Button } from "@bounce/web-components"

import { HeroMap } from "./HeroMap"
import { SearchBar } from "../SearchBar/SearchBar"
import type { InteractiveMapProps as BaseInteractiveMapProps } from "@/components/LuggageStorage/Map/Interactive/InteractiveMap"
import { useStoreSuggestions } from "@/components/LuggageStorage/Store/hooks/useStoreSuggestions"
import type { AggregateRating } from "@/libs/bounce/types"
import type { PageType } from "@/types"

import ArrowRightIcon from "@bounce/assets/icons/ArrowRight.svg"
import LocationPinFilledIcon from "@bounce/assets/icons/LocationPinFilled.svg"
import LockFilledIcon from "@bounce/assets/icons/LockFilled.svg"
import StarFilledIcon from "@bounce/assets/icons/Star-filled.svg"

type InteractiveMapProps = Pick<BaseInteractiveMapProps, "coordinates" | "nearestStores">

export type HeroCityPoiProps = {
  aggregateRating: AggregateRating
  className?: string
  cta: string
  from: PageType
  numLocations: string
  placeName?: string
  query: string
  subtitle: string
  title: string | React.ReactNode
  searchUrl: string
} & InteractiveMapProps

type StatItemProps = {
  Icon: React.FC<{ className: string }>
  children: React.ReactNode
}

const StatItem = ({ Icon, children }: StatItemProps) => {
  return (
    <div className="flex items-center">
      <Icon className="mr-1 size-[13px] text-secondary-600 md:size-5" />
      <Caption className="font-light md:text-[18px]">{children}</Caption>
    </div>
  )
}

export const HeroCityPoi = ({
  aggregateRating,
  className,
  cta,
  from,
  numLocations,
  placeName,
  query,
  subtitle,
  title,
  coordinates,
  nearestStores,
  searchUrl,
}: HeroCityPoiProps) => {
  const { t, i18n } = useTranslation()
  const bounceGlobals = useBounceGlobalValues()
  const { format: ratingFormat } = intlNumberFormat({ locale: i18n.language, options: { maximumFractionDigits: 1 } })
  const { format: countFormat } = intlNumberFormat({ locale: i18n.language, options: {} })
  const { handleGetStoreUrl } = useStoreSuggestions({
    from,
    searchQuery: query,
  })

  const RatingStatItem = ({ rating, reviewCount }: { rating: number; reviewCount: number }) => (
    <StatItem Icon={StarFilledIcon}>
      <Trans
        components={{
          bold: <b className="font-bold" />,
          hideOnSmall: <span className="hidden xs:inline" />,
        }}
        i18nKey="cmp.heroCityPoi.rated3"
        defaults="<bold>{{appRating}}/5</bold> ({{numAppReviews}}+<hideOnSmall> reviews</hideOnSmall>)"
        values={{
          appRating: ratingFormat(rating),
          numAppReviews: countFormat(reviewCount),
        }}
      />
    </StatItem>
  )

  const LocationStatItem = ({ numLocations }: { numLocations: string }) => (
    <StatItem Icon={LocationPinFilledIcon}>
      <Trans
        components={{ bold: <b className="font-bold" /> }}
        i18nKey="cmp.heroCityPoi.numLocations2"
        defaults="<bold>{{STORAGE_SPOT_COUNT}}+ </bold>locations"
        values={{ STORAGE_SPOT_COUNT: numLocations }}
      />
    </StatItem>
  )

  const BagsStoredStatItem = () => (
    <StatItem Icon={LockFilledIcon}>
      <Trans
        components={{ bold: <b className="font-bold" /> }}
        i18nKey="cmp.heroCityPoi.bagsStoredV3"
        defaults="<bold>{{numBags}}</bold> bags stored worldwide"
        values={{
          numBags: bounceGlobals.numBagsStoredLong,
        }}
      />
    </StatItem>
  )

  return (
    <header className={classNames("relative w-full bg-primary-100", className)}>
      <HeroMap
        coordinates={coordinates}
        nearestStores={nearestStores}
        searchUrl={searchUrl}
        query={query}
        getStoreUrl={handleGetStoreUrl}
        visible={false}
      />

      <div className="md:left-align mx-auto flex max-w-screen-xl flex-col items-center text-center lg:flex-row-reverse lg:p-0 lg:py-4 lg:text-left">
        <div className="hidden w-full md:flex md:flex-1 lg:pr-6">
          <HeroMap
            coordinates={coordinates}
            nearestStores={nearestStores}
            searchUrl={searchUrl}
            query={query}
            getStoreUrl={handleGetStoreUrl}
            visible={true}
          />
        </div>
        <div className="relative z-1 mx-auto mt-0 flex max-w-screen-xl flex-col px-6 md:mt-4 lg:mt-[72px] lg:pb-16">
          <div>
            <div className="mt-2 flex size-full max-w-full flex-col pr-0 md:mt-0 md:pr-3.5">
              <h1 className="title4 mb-3 max-w-[500px] md:title1">{title}</h1>
              <p className="paragraph1">{subtitle}</p>
              <div className="ml-4 mt-8 flex w-full items-center justify-between md:mt-12 lg:max-w-[500px]">
                <div className="flex-start mr-2 flex items-center">
                  <LocationStatItem numLocations={numLocations} />
                </div>
                <div className="flex-end mr-6 flex items-center md:mr-8">
                  <RatingStatItem rating={aggregateRating.rating} reviewCount={aggregateRating.count} />
                </div>
              </div>
              <SearchBar
                from={from}
                address={placeName}
                initialQuery={query}
                className="mt-3"
                isDatepickerVisible
                button={
                  <Button
                    className="mt-8 lg:mt-4"
                    RightIcon={ArrowRightIcon}
                    iconClassName="size-5"
                    title={t("cmp.heroCityPoi.searchPlaceName", "Search {{placeName}}", {
                      placeName,
                    })}>
                    {cta}
                  </Button>
                }
              />
              <div className="mt-4 flex w-full items-center justify-center lg:ml-4 lg:justify-start">
                <BagsStoredStatItem />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 z-0 h-14 bg-white md:h-16 lg:hidden"></div>
    </header>
  )
}
